import { useDeploymentStore } from '@/store/deploymentStore';
import { CognitoService } from '@/auth/CognitoService';
import { getDeviceId } from '@/utils/deviceId';
import { GuidedLLMError } from '@/llm/guidedClient';
import type { BuildInputs } from '@/build-core';
import type { AppDefinition } from '@/types/app';

export interface BuildStatusResponse {
  status: 'queued' | 'running' | 'done' | 'error';
  phase: string;
  definition?: AppDefinition;
  error?: string;
  summary?: string;
}

function base(): string {
  return (useDeploymentStore.getState().config?.apiBaseUrl ?? '').replace(/\/+$/, '');
}
function authHeaders(signedIn: boolean): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (signedIn) { const t = CognitoService.getAccessTokenSync(); if (t) h.Authorization = `Bearer ${t}`; }
  else h['x-device-id'] = getDeviceId();
  return h;
}
function mapError(status: number, reason?: string): GuidedLLMError {
  if (status === 402 || reason === 'insufficient_credits') return new GuidedLLMError('insufficient_credits');
  if (status === 401 || reason === 'signup_required') return new GuidedLLMError('signup_required');
  if (status === 409) return new GuidedLLMError('unavailable', 'A build is already in progress.');
  return new GuidedLLMError('unavailable', `Build request failed (${status})`);
}

/** Create a server build job. Resolves on 202; throws a mapped GuidedLLMError otherwise. */
export async function createBuild(buildId: string, inputs: BuildInputs, signedIn: boolean): Promise<void> {
  let res: Response;
  try { res = await fetch(`${base()}/builds`, { method: 'POST', headers: authHeaders(signedIn), body: JSON.stringify({ buildId, inputs }) }); }
  catch { throw new GuidedLLMError('network', 'Network request failed'); }
  if (res.status === 202 || res.ok) return;
  let reason: string | undefined; try { reason = (await res.json())?.error; } catch { /* ignore */ }
  throw mapError(res.status, reason);
}

/** Poll a build job's status. */
export async function getBuild(buildId: string, signedIn: boolean): Promise<BuildStatusResponse> {
  const res = await fetch(`${base()}/builds/${encodeURIComponent(buildId)}`, { headers: authHeaders(signedIn) });
  if (!res.ok) throw new GuidedLLMError('unavailable', `Build status failed (${res.status})`);
  return (await res.json()) as BuildStatusResponse;
}
